import { Calendar, Link, User } from 'lucide-react';
import clsx from 'clsx';
import { useConfigStore } from '../../store/useConfigStore';
import { itemRef } from '../../utils/itemRef';
import { CheckoutBadge } from '../workitems/CheckoutBadge';
import type { WorkItem } from '../../types';

interface Props {
  item: WorkItem;
  isDragging: boolean;
}

export default function KanbanCard({ item, isDragging }: Props) {
  const { getWorkItemTypeConfig, getPriorityColor } = useConfigStore();
  const typeConfig = getWorkItemTypeConfig(item.workItemType);

  return (
    <div
      className={clsx(
        'bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-3 cursor-pointer transition-shadow',
        isDragging ? 'shadow-lg ring-2 ring-accent/30' : 'hover:shadow-md'
      )}
    >
      {/* Type badge */}
      <div className="flex items-center gap-2 mb-2">
        <span
          className="px-1.5 py-0.5 text-xs font-medium rounded"
          style={{
            backgroundColor: typeConfig.color + '15',
            color: typeConfig.color,
          }}
        >
          {typeConfig.label}
        </span>
        {itemRef(item) && (
          <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">{itemRef(item)}</span>
        )}

        {/* Priority dot */}
        <div
          className="w-2 h-2 rounded-full ml-auto"
          style={{ backgroundColor: getPriorityColor(item.priority) }}
          title={item.priority}
        />
      </div>

      {/* Title */}
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 mb-2">
        {item.title}
      </p>

      {/* Footer */}
      <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
        {item.assignee && (
          <div className="flex items-center gap-1">
            <User size={10} />
            <span className="truncate max-w-16">{item.assignee}</span>
          </div>
        )}

        <CheckoutBadge checkout={item.checkout} compact />

        {item.dueDate && (
          <div className="flex items-center gap-1">
            <Calendar size={10} />
            <span>{new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          </div>
        )}

        {/* Optional-chained deliberately: the board is served by a Mango field
            projection, and CouchDB omits a key entirely when the document does
            not have it — so any item predating this field arrives without it.
            A card must never crash the board over one missing optional field. */}
        {(item.dependencies?.length ?? 0) > 0 && (
          <div className="flex items-center gap-1 ml-auto">
            <Link size={10} />
            <span>{item.dependencies?.length}</span>
          </div>
        )}
      </div>
    </div>
  );
}
